import { type Board, type Dir, dirOf, pack } from "./board";
import { PICTURES, type PictureId } from "./pictures";
import { Rng } from "./rng";
import { layout } from "./shared";
import { ANIMALS, type Animal, BIG_SPEC, animalMask, animalOf, animalRatio } from "./animals";
import { HANDMADE } from "./stages";

/**
 * نقشه‌ی بزرگِ همکاری (کاربر، ۷ آبان: «مرحله‌های زیاد و نقشه‌ی بزرگ که هر قسمتش با همکاریِ دو طرف باز می‌شود»؛
 * انتخابِ کاربر: یک نقشه‌ی بزرگ در حدودِ ۹ تکه، ۲ تا ۴ نفر یک تیم). نقشه ۳×۳ تکه است، مثلِ فرشِ لچک‌ترنج: ترنجِ
 * وسط، چهار نقشِ کناری رو به بیرون، چهار نقشِ گوشه. اول فقط تکه‌ی وسط باز است؛ با تمام‌شدنش چهار تکه‌ی کناری
 * باز می‌شوند و هر گوشه وقتی که هر دو کناری‌اش تمام شده باشد. در هر تکه فلش‌های همه‌ی بازیکن‌ها لای هم‌اند و
 * راهِ هم را می‌بندند — هر کس فقط فلش‌های رنگِ خودش را می‌زند، پس تکه فقط با همکاری باز می‌شود.
 *
 * بی‌گره بودن از «موج» است: تکه‌ی موجِ w (فاصله تا وسط) وقتی باز می‌شود که همسایه‌های موجِ w−۱ اش تمام شده
 * باشند. نوکِ فلش فقط رو به سویی است که راهش تا لبه‌ی نقشه یا از تکه‌های تمام‌شده می‌گذرد یا از تکه‌های هنوز بسته
 * (که فلشی در میان ندارند): وسط هر چهار سو، کناری هر سو جز رو به وسط، گوشه فقط دو سوی بیرون. پس تکه‌های باز
 * هرگز جلوی هم را نمی‌گیرند و هر تکه همان پازلِ تنهای خودش است، روی تخته‌ی خودش ساخته (pack) و جابه‌جا شده.
 *
 * آینه‌ی coop.go و ArrowsCoop.cs ِ اپ؛ testdata/arrows_vectors.json هر سه را می‌سنجد.
 */
export interface CoopSpec {
  /** فلش‌های هر تکه، همه‌ی بازیکن‌ها روی هم. */
  arrows: number;
  len: number;
  turn?: number;
}

export interface CoopPart {
  /** گوشه‌ی بالا-چپِ تکه روی نقشه و پهنا و بلندیش (خانه) — فرش مربع، جانور به نسبتِ خودش. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** ۰ وسط، ۱ کناری، ۲ گوشه. */
  wave: number;
  /** تکه‌هایی که باید تمام شوند تا این باز شود. */
  needs: number[];
  /** نامِ نقشِ تکه (نقشه‌ی جانور: شناسه‌ی جانور). */
  pic: string;
}

export interface CoopBoard extends Board {
  parts: CoopPart[];
  /** تکه‌ی هر فلش، به شماره‌ی فلش. */
  partOf: number[];
}

/** ۳×۳: شماره‌ی تکه = سطر × ۳ + ستون. */
export const PARTS = 9;
const GAP = 1;

/** سوهای مجازِ نوک در هر تکه (۰ بالا، ۱ راست، ۲ پایین، ۳ چپ). */
const DIRS: readonly (readonly Dir[])[] = [
  [3, 0], [0, 1, 3], [0, 1],
  [0, 2, 3], [0, 1, 2, 3], [0, 1, 2],
  [2, 3], [1, 2, 3], [1, 2],
];
/** چرخشِ نقشِ هر تکه، به ربع‌دورِ ساعت‌گرد: کناری‌ها و گوشه‌ها رو به بیرون. */
const TURNS = [3, 0, 0, 3, 0, 1, 2, 2, 1];
const NEEDS: readonly number[][] = [[1, 3], [4], [1, 5], [4], [], [4], [3, 7], [4], [5, 7]];

const cellsPerArrow = (len: number) => 0.9 * (2 + len) / 2;
const REGIONS = { rot4: 4, mir4: 4, mir2: 2, rot2: 2 } as const;

/** نقش‌های ترنج و گوشه: چهاربرشی‌ها، به ترتیبِ HANDMADE. */
const FOUR = HANDMADE.map(([id]) => id).filter((id) => REGIONS[PICTURES[id].sym] === 4);
const ALL = HANDMADE.map(([id]) => id);

/** نقش‌های سه جایگاه (وسط، کناری، گوشه) از بذر؛ هیچ دو تا یکی نیستند. */
function pictures(rng: Rng): [PictureId, PictureId, PictureId] {
  const center = FOUR[rng.next(FOUR.length)]!;
  let e = rng.next(ALL.length);
  while (ALL[e] === center) e = (e + 1) % ALL.length;
  let c = rng.next(FOUR.length);
  while (FOUR[c] === center || FOUR[c] === ALL[e]) c = (c + 1) % FOUR.length;
  return [center, ALL[e]!, FOUR[c]!];
}

/** خانه‌های نقشِ pic روی تخته‌ی n×n (بی نقشِ وسط)، k ربع‌دور چرخیده. */
function mask(pic: PictureId, cells: number, turns: number): { n: number; ok: boolean[] } {
  const L = layout(PICTURES[pic], cells);
  const n = L.n;
  let ok = Array.from(L.reg, (r) => r >= 0);
  for (let k = 0; k < turns; k++) {
    const next = new Array<boolean>(n * n).fill(false);
    for (let c = 0; c < n * n; c++) if (ok[c]) next[(c % n) * n + (n - 1 - Math.floor(c / n))] = true;
    ok = next;
  }
  return { n, ok };
}

interface Tile { w: number; h: number; ok: boolean[] }

/**
 * نقشه‌ی جانور (animals.ts): کاشی‌ها به نسبتِ خودِ جانور (بلندیِ t، پهنای نزدیکِ t × نسبت)، پس جانورِ بلند (غزال) هم
 * همه‌ی نقشه را پر می‌کند. جانور روی کلِ نقشه‌ی (۳پهنا+۲)×(۳بلندی+۲) کشیده می‌شود و هر تکه همان بخشِ جانور است که زیرِ
 * کاشیِ خودش افتاده — خطِ میانِ کاشی‌ها خالی است. t آن‌قدر بزرگ می‌شود که همه‌ی تکه‌ها با هم جای ۹ تکه فلش داشته
 * باشند؛ کاشیِ کم‌خانه کم‌فلش می‌ماند، و تکه‌ی بی‌فلش همان اول تمام است.
 */
function animalTiles(a: Animal, spec: CoopSpec, gap: number): { mw: number; mh: number; masks: Tile[] } {
  const want = PARTS * spec.arrows * cellsPerArrow(spec.len);
  const ratio = animalRatio(a);
  for (let t = 3; ; t++) {
    const tw = Math.max(2, Math.round(t * ratio));
    const cols = 3 * tw + 2 * gap, rows = 3 * t + 2 * gap;
    const big = animalMask(a, cols, rows);
    const masks = Array.from({ length: PARTS }, (_, p) => {
      const ok = new Array<boolean>(tw * t).fill(false);
      for (let y = 0; y < t; y++) for (let x = 0; x < tw; x++) {
        ok[y * tw + x] = big[(Math.floor(p / 3) * (t + gap) + y) * cols + (p % 3) * (tw + gap) + x]!;
      }
      return { w: tw, h: t, ok };
    });
    if (masks.reduce((k, mk) => k + mk.ok.filter(Boolean).length, 0) >= want || t >= 120) return { mw: tw, mh: t, masks };
  }
}

/**
 * نقشه‌ی همکاری از بذر. animal: شماره‌ی جانور در ANIMALS (نقشه‌ی جانور)، یا −۱ برای فرشِ لچک‌ترنج.
 */
export function generateCoop(seed: number, players: number, spec: CoopSpec, animal = -1): CoopBoard {
  const rng = new Rng(seed);
  const beast = animal >= 0 ? ANIMALS[animal] : undefined;
  let masks: Tile[], mw: number, mh: number, picOf: (p: number) => string;
  if (beast) {
    ({ mw, mh, masks } = animalTiles(beast, spec, GAP));
    picOf = () => beast.id;
  } else {
    const [center, edge, corner] = pictures(rng);
    const per = (pic: PictureId) => Math.round((spec.arrows / REGIONS[PICTURES[pic].sym]) * cellsPerArrow(spec.len));
    const picBy = (p: number) => (p === 4 ? center : p % 2 === 1 ? edge : corner);
    masks = Array.from({ length: PARTS }, (_, p) => { const k = mask(picBy(p), per(picBy(p)), TURNS[p]!); return { w: k.n, h: k.n, ok: k.ok }; });
    mw = mh = Math.max(...masks.map((k) => k.w));
    picOf = picBy;
  }
  return packParts(rng, players, spec, masks, mw, mh, GAP, picOf);
}

/**
 * تکه‌ها را جدا جدا می‌چیند (هر کدام روی تخته‌ی خودش، با سوهای مجازِ همان جایگاه) و کنارِ هم می‌گذارد. players ۰ =
 * بی‌صاحب (نقشه‌ی بزرگِ تک‌نفره)، و آن‌وقت بذر برای رنگ‌ها خرج نمی‌شود.
 */
function packParts(rng: Rng, players: number, spec: CoopSpec, masks: Tile[], mw: number, mh: number, gap: number,
  picOf: (p: number) => string): CoopBoard {
  const w = 3 * mw + 2 * gap, h = 3 * mh + 2 * gap;
  const arrows: Board["arrows"] = [];
  const partOf: number[] = [];
  const parts: CoopPart[] = [];
  for (let p = 0; p < PARTS; p++) {
    const { w: pw, h: ph, ok } = masks[p]!;
    const ox = (p % 3) * (mw + gap) + Math.floor((mw - pw) / 2), oy = Math.floor(p / 3) * (mh + gap) + Math.floor((mh - ph) / 2);
    parts.push({ x: ox, y: oy, w: pw, h: ph, wave: Math.abs((p % 3) - 1) + Math.abs(Math.floor(p / 3) - 1), needs: NEEDS[p]!, pic: picOf(p) });
    const dirs = DIRS[p]!;
    const tier = { w: pw, h: ph, minLen: 2, maxLen: spec.len, fill: 0.93, turn: spec.turn ?? 0.45 };
    const allow = (c: readonly number[]) => dirs.includes(dirOf(pw, c));
    let b = pack(tier, ok, rng, allow);
    for (let i = 0; i < 6 && b.arrows.length < spec.arrows * 0.85; i++) {
      const again = pack(tier, ok, rng, allow);
      if (again.arrows.length > b.arrows.length) b = again;
    }
    // رنگ‌ها به نوبت، از جایی که بذر می‌گوید: سهمِ همه برابر (±۱) و لای هم
    const off = players > 0 ? rng.next(players) : 0;
    b.arrows.forEach((a, i) => {
      arrows.push({
        id: arrows.length, ...(players > 0 ? { owner: (i + off) % players } : {}),
        cells: a.cells.map((c) => (oy + Math.floor(c / pw)) * w + ox + (c % pw)),
      });
      partOf.push(p);
    });
  }
  return { w, h, arrows, parts, partOf };
}

/** تکه‌های تمام‌شده: هیچ فلشِ زنده‌ای ندارند. gone[شماره‌ی فلش] = برداشته شده. */
export function partsDone(b: CoopBoard, gone: readonly boolean[]): boolean[] {
  const left = new Array<number>(PARTS).fill(0);
  b.partOf.forEach((p, id) => { if (!gone[id]) left[p]!++; });
  return left.map((n) => n === 0);
}

/** تکه‌های باز: همه‌ی پیش‌نیازهایشان تمام شده. */
export function partsOpen(b: CoopBoard, done: readonly boolean[]): boolean[] {
  return b.parts.map((p) => p.needs.every((q) => done[q]));
}

/** فلش‌های زنده‌ی روی تخته: تکه‌اش باز است و هنوز نرفته. */
export function coopAlive(b: CoopBoard, gone: readonly boolean[]): boolean[] {
  const open = partsOpen(b, partsDone(b, gone));
  return b.arrows.map((a) => open[b.partOf[a.id]!]! && !gone[a.id]);
}

/**
 * نقشه‌ی بزرگِ تک‌نفره‌ی جانور (آفلاین): همان ۳×۳ کاشی، ولی بی خطِ میانِ کاشی‌ها و بی رنگ، پس جانور یک‌تکه دیده می‌شود
 * (کاربر، ۸ آبان: «دست‌کم چهار برابرِ این بزرگ‌تر»). چیدنِ یک‌جا روی تخته‌ی این‌قدر بزرگ گره می‌خورد و نیمی از فلش‌ها را
 * از دست می‌داد؛ کاشی‌به‌کاشی پر و تند است. بی‌گره بودن از همان سوهای جایگاه است: گوشه فقط رو به بیرون، کناری هرگز رو به
 * وسط، پس گوشه‌ها جلوی هیچ‌کس نیستند، کناری‌ها فقط پشتِ گوشه‌ها گیر می‌کنند و وسط پشتِ کناری‌ها — از بیرون به درون
 * همیشه باز می‌شود.
 */
export function generateAnimal(seed: number, id: string, spec: { arrows: number; len: number; turn?: number } = BIG_SPEC): Board {
  const a = animalOf(id) ?? ANIMALS[0]!;
  const rng = new Rng(seed);
  const per: CoopSpec = { arrows: Math.ceil(spec.arrows / PARTS), len: spec.len, turn: spec.turn };
  const { mw, mh, masks } = animalTiles(a, per, 0);
  const b = packParts(rng, 0, per, masks, mw, mh, 0, () => a.id);
  return { w: b.w, h: b.h, arrows: b.arrows };
}
